import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";

const ReservarEspacio = () => {
  const navigate = useNavigate();

  const [alerta, setAlerta] = useState({
    mostrar: false,
    mensaje: "",
    tipo: "success",
  });

  const [espacios, setEspacios] = useState([]);
  const [loadingEspacios, setLoadingEspacios] = useState(true);

  const [form, setForm] = useState({
    espacio_id: "",
    fecha: "",
    hora_inicio: "",
    hora_fin: "",
    tipo_piscina: "",
    observacion: "",
  });

  const [ocupados, setOcupados] = useState([]);
  const [loadingOcupados, setLoadingOcupados] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const mostrarAlerta = (mensaje, tipo = "success") => {
    setAlerta({ mostrar: true, mensaje, tipo });
    setTimeout(() => {
      setAlerta({ mostrar: false, mensaje: "", tipo: "success" });
    }, 3000);
  };

  // Cargar espacios disponibles
  const cargarEspacios = async () => {
    setLoadingEspacios(true);
    try {
      const response = await axios.get("/espacios");
      setEspacios(response.data.espacios || []);
    } catch (error) {
      console.error("Error al cargar espacios:", error);
      mostrarAlerta("Error al cargar los espacios", "danger");
    } finally {
      setLoadingEspacios(false);
    }
  };

  useEffect(() => {
    cargarEspacios();
  }, []);

  // Cargar horarios ocupados cada vez que cambia espacio o fecha
  useEffect(() => {
    const cargarOcupados = async () => {
      if (!form.espacio_id || !form.fecha) {
        setOcupados([]);
        return;
      }
      setLoadingOcupados(true);
      try {
        const response = await axios.get("/ocupados-admin", {
          params: { espacio_id: form.espacio_id, fecha: form.fecha },
        });
        setOcupados(response.data.horarios || []);
      } catch (error) {
        console.error("Error al cargar horarios ocupados:", error);
        mostrarAlerta("Error al consultar horarios ocupados", "danger");
      } finally {
        setLoadingOcupados(false);
      }
    };

    cargarOcupados();
  }, [form.espacio_id, form.fecha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const limpiarFormulario = () => {
    setForm({
      espacio_id: "",
      fecha: "",
      hora_inicio: "",
      hora_fin: "",
      tipo_piscina: "",
      observacion: "",
    });
    setOcupados([]);
  };

  const crearReserva = async () => {
    if (!form.espacio_id || !form.fecha) {
      mostrarAlerta("Espacio y fecha son obligatorios", "danger");
      return;
    }

    if ((form.hora_inicio && !form.hora_fin) || (!form.hora_inicio && form.hora_fin)) {
      mostrarAlerta("Debes ingresar ambas horas o ninguna", "danger");
      return;
    }

    if (form.hora_inicio && form.hora_fin && form.hora_inicio >= form.hora_fin) {
      mostrarAlerta("La hora de inicio debe ser menor que la hora de fin", "danger");
      return;
    }

    setEnviando(true);
    try {
      await axios.post("/reservas-admin", {
        espacio_id: form.espacio_id,
        fecha: form.fecha,
        hora_inicio: form.hora_inicio || null,
        hora_fin: form.hora_fin || null,
        tipo_piscina: form.tipo_piscina || null,
        observacion: form.observacion || null,
      });

      mostrarAlerta("Reserva creada correctamente");
      limpiarFormulario();
    } catch (error) {
      console.error("Error al crear reserva:", error);
      mostrarAlerta(
        error.response?.data?.message || "Error al crear la reserva",
        "danger"
      );
    } finally {
      setEnviando(false);
    }
  };

  const espacioSeleccionado = espacios.find(
    (e) => String(e.id) === String(form.espacio_id)
  );

  return (
    <div className="container-fluid bg-light min-vh-100 py-4 px-3 px-md-4 px-lg-5">
      {alerta.mostrar && (
        <div
          className={`alert alert-${alerta.tipo} alert-dismissible fade show shadow position-fixed`}
          style={{ top: "20px", right: "20px", zIndex: 9999, minWidth: "300px" }}
        >
          <div className="d-flex align-items-center">
            <i
              className={`bi me-2 ${
                alerta.tipo === "success" ? "bi-check-circle-fill" : "bi-x-circle-fill"
              }`}
            ></i>
            <span>{alerta.mensaje}</span>
          </div>
          <button
            className="btn-close"
            onClick={() => setAlerta({ ...alerta, mostrar: false })}
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="card shadow-sm border-0 mb-4 border-start border-secondary border-4 rounded-3">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-outline-secondary border-0 rounded-circle p-2"
              onClick={() => navigate("/DashboardSecretaria")}
              title="Volver al dashboard"
            >
              <i className="bi bi-arrow-left-short fs-4"></i>
            </button>
            <h4 className="fw-bold mb-0 text-dark">
              <i className="bi bi-calendar-check me-2 text-secondary"></i>
              Reservar Espacios
            </h4>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Formulario */}
        <div className="col-lg-7">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-body p-4">
              <div className="mb-3">
                <label className="form-label fw-medium">Espacio</label>
                <select
                  className="form-select"
                  name="espacio_id"
                  value={form.espacio_id}
                  onChange={handleChange}
                  disabled={loadingEspacios}
                >
                  <option value="">Selecciona un espacio</option>
                  {espacios.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Fecha</label>
                <input
                  type="date"
                  className="form-control"
                  name="fecha"
                  value={form.fecha}
                  onChange={handleChange}
                />
              </div>

              <div className="row">
                <div className="col-6 mb-3">
                  <label className="form-label fw-medium">Hora inicio</label>
                  <input
                    type="time"
                    className="form-control"
                    name="hora_inicio"
                    value={form.hora_inicio}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label fw-medium">Hora fin</label>
                  <input
                    type="time"
                    className="form-control"
                    name="hora_fin"
                    value={form.hora_fin}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <small className="text-muted d-block mb-3">
                Deja las horas en blanco si la reserva es para todo el día.
              </small>

              <div className="mb-3">
                <label className="form-label fw-medium">Tipo de piscina</label>
                <input
                  className="form-control"
                  placeholder="Ej: Temperada"
                  name="tipo_piscina"
                  value={form.tipo_piscina}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Observación</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name="observacion"
                  value={form.observacion}
                  onChange={handleChange}
                ></textarea>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={limpiarFormulario}
                  disabled={enviando}
                >
                  Limpiar
                </button>
                <button
                  className="btn btn-primary px-4 d-flex align-items-center gap-2"
                  onClick={crearReserva}
                  disabled={enviando}
                >
                  {enviando ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></span>
                  ) : (
                    <i className="bi bi-check-lg"></i>
                  )}
                  Reservar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Horarios ocupados */}
        <div className="col-lg-5">
          <div className="card shadow-sm border-0 rounded-3 h-100">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-clock-history me-2 text-secondary"></i>
                Horarios ocupados
                {espacioSeleccionado && (
                  <span className="text-muted fw-normal">
                    {" "}
                    — {espacioSeleccionado.nombre}
                  </span>
                )}
              </h6>

              {!form.espacio_id || !form.fecha ? (
                <p className="text-muted small mb-0">
                  Selecciona un espacio y una fecha para ver los horarios ya
                  reservados.
                </p>
              ) : loadingOcupados ? (
                <div className="text-center py-4">
                  <div
                    className="spinner-border spinner-border-sm text-secondary"
                    role="status"
                  ></div>
                </div>
              ) : ocupados.length === 0 ? (
                <p className="text-success small mb-0">
                  <i className="bi bi-check-circle me-1"></i>
                  No hay horarios ocupados para esta fecha.
                </p>
              ) : (
                <ul className="list-group list-group-flush">
                  {ocupados.map((h, idx) => (
                    <li
                      key={idx}
                      className="list-group-item px-0 d-flex align-items-center gap-2"
                    >
                      <i className="bi bi-lock-fill text-danger"></i>
                      {h.hora_inicio ? String(h.hora_inicio).slice(0, 5) : "—"}
                      {" – "}
                      {h.hora_fin ? String(h.hora_fin).slice(0, 5) : "—"}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservarEspacio;